import differenceInDays from 'date-fns/differenceInDays'
import React, { FC } from 'react'

import { LeadSource } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDateRange } from '@avantstay/format-date-range'

import { backgroundColorByLeadSource, iconByLeadSourceCircle } from '../../../../constants'
import { getReviewLeadSource, parseISODate, pluralize } from '../../../../utils'
import { MMedium } from '../../../Typography'
import { BookingPeriodProps } from '../../ReviewTimeline.types'
import * as S from './ReviewTimelineHeader.styles'

interface ReviewHeaderProps {
  bookingURL?: string
  guestName: string
  period: BookingPeriodProps
  homeName?: string
  source: LeadSource
  shouldUseSourceIcon?: boolean
  onGuestNameClick?: () => void
}

export const ReviewTimelineHeader: FC<ReviewHeaderProps> = ({
  bookingURL,
  guestName,
  homeName,
  period,
  source,
  shouldUseSourceIcon = false,
  onGuestNameClick,
}) => {
  const numberOfNights = differenceInDays(parseISODate(period.end), parseISODate(period.start))
  const { label: leadSourceLabel, badgeColor: leadSourceBadgeColor } = getReviewLeadSource(source)

  const Icon =
    shouldUseSourceIcon && source && iconByLeadSourceCircle[source]
      ? iconByLeadSourceCircle[source]?.icon
      : S.IconCalendarStyled
  const backgroundColor =
    shouldUseSourceIcon && source && backgroundColorByLeadSource[source]
      ? backgroundColorByLeadSource[source]
      : undefined

  const clickableGuestNameProps = {
    role: 'button',
    tabIndex: 0,
    onClick: onGuestNameClick,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onGuestNameClick?.()
      }
    },
  }

  return (
    <S.ReviewHeaderWrapper $gridTemplateColumns={shouldUseSourceIcon ? '36px 1fr' : undefined}>
      {!shouldUseSourceIcon ? (
        <S.BadgeStyled badgeColor={leadSourceBadgeColor}>
          {leadSourceLabel}
        </S.BadgeStyled>
      ) : null}

      <S.CalendarIconContainer $backgroundColor={backgroundColor}>
        <Icon />
      </S.CalendarIconContainer>
      <S.BookingInfo>
        <S.GuestName
          $clickable={!!onGuestNameClick}
          {...(onGuestNameClick ? clickableGuestNameProps : {})}
        >
          {guestName}
        </S.GuestName>
        {bookingURL ? (
          <S.DefaultLink href={bookingURL} target="_blank">
            {formatDateRange(parseISODate(period.start), parseISODate(period.end))} &bull;{' '}
            {numberOfNights} {pluralize(numberOfNights, 'nights', 'night')}
          </S.DefaultLink>
        ) : (
          <MMedium>
            {formatDateRange(parseISODate(period.start), parseISODate(period.end))} &bull;{' '}
            {numberOfNights} {pluralize(numberOfNights, 'nights', 'night')}
          </MMedium>
        )}
        {homeName ? <MMedium>{homeName}</MMedium> : null}
      </S.BookingInfo>
    </S.ReviewHeaderWrapper>
  )
}
